/**
 * Reload onto whatever build is now live. A plain reload would just re-serve
 * the old precached shell, so this forces the waiting worker to take over
 * first (reg.update() → SKIP_WAITING → controllerchange) and only then
 * reloads. Falls through to a plain reload if anything here doesn't resolve
 * in time — a stale reload beats hanging on the banner forever.
 */
export async function applyUpdate(currentVersion?: string, latestVersion?: string): Promise<void> {
  // On builds with no real service worker (e.g. PR previews - see
  // vite.config.ts's `selfDestroying`), the worker unregisters itself and
  // `ready` never resolves. Time out rather than hang the banner forever.
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 5000)),
  ]);
  if (!reg) {
    location.reload();
    return;
  }
  const installed = waitForUpdatedWorker(reg);

  try {
    await reg.update();
  } catch {
    // A rejected check shouldn't skip straight to a stale reload — there may
    // already be a waiting worker from an earlier, successful check.
  }

  await installed;

  if (reg.waiting) {
    const controllerChanged = new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
    });
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
    await Promise.race([controllerChanged, new Promise((resolve) => setTimeout(resolve, 5000))]);
    location.reload();
    return;
  }

  // Still nothing waiting, and the build we already know is live doesn't
  // match what we're running — the worker never picked it up. Unregistering
  // forces the next load to hit the network instead of the precached shell,
  // which is the only way to guarantee a fresh fetch rather than another
  // reload onto the same stale build.
  if (latestVersion !== undefined && latestVersion !== currentVersion) {
    await reg.unregister();
  }

  location.reload();
}

/**
 * A worker found by reg.update() starts in "installing", and only once it
 * reaches "installed" is it exposed as reg.waiting — reading reg.waiting
 * right after update() resolves can race a worker that's still compiling.
 */
function waitForUpdatedWorker(reg: ServiceWorkerRegistration): Promise<void> {
  if (reg.waiting) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      reg.removeEventListener("updatefound", onUpdateFound);
      resolve();
    };

    const onUpdateFound = () => {
      const worker = reg.installing;
      if (!worker || worker.state === "installed" || worker.state === "activated") {
        finish();
        return;
      }
      const onStateChange = () => {
        if (worker.state === "installed" || worker.state === "activated" || worker.state === "redundant") {
          worker.removeEventListener("statechange", onStateChange);
          finish();
        }
      };
      worker.addEventListener("statechange", onStateChange);
    };

    reg.addEventListener("updatefound", onUpdateFound);
    setTimeout(finish, 5000);
  });
}
